/**
 * @copyright Copyright 2021-2025 Epic Systems Corporation
 * @file dashboard data and APIs
 * @module Epic.AppOrchard.Dashboard.Data
 */

import { INotImplementingNowDecision } from "ao/types";
import {
	IAvailableDashboards,
	IGetAttestationResponseModel,
	IHomegrownAppsForOrgResponseModel,
	IInProgressAppsForOrgResponseModel,
	ILiveAppsForOrgResponseModel,
	INominatedAppsForOrgResponseModel,
	ISaveDefaultDashboardResponse,
	ISaveWidgetPrefsResponse,
	IWidget,
	IWidgetPreferences,
} from "ao/types/dashboard";
import { IUpdateCustomerAppLicenseRequest } from "../../../data";
import api from "../../../utils/api";
import { HomegrownAppsWidget, InProgressAppsWidget, LiveAppsWidget, NominatedAppsWidget } from "../widgets";

/**
 * Load all dashboards available to a user (live + user has security to access) from the server
 */
export async function getAvailableDashboards(): Promise<IAvailableDashboards> {
	const data: IAvailableDashboards = await api.get<IAvailableDashboards>("/api/Dashboard/LoadAvailable");
	return data;
}

/**
 * Save user's dashboard preferences (widgets to show, ordering) to the server
 * @param dashboardDescriptor descriptor to identify dashboard prefs are being saved for
 * @param prefs widget prefs to save
 * @returns
 */
export async function saveDashboardPreferences(
	dashboardDescriptor: string,
	prefs: IWidgetPreferences[],
): Promise<ISaveWidgetPrefsResponse> {
	const response: ISaveWidgetPrefsResponse = await api.post<ISaveWidgetPrefsResponse>(
		"/api/Dashboard/UpdatePreferences",
		{ Descriptor: dashboardDescriptor, WidgetPreferences: prefs },
	);
	return response;
}

/**
 * Set the dashboard as the user's default dashboard for display
 * @param dashboardDescriptor descriptor to identify the dashboard to the server
 * @returns
 */
export async function setDashboardAsDefault(
	dashboardDescriptor: string,
): Promise<ISaveDefaultDashboardResponse> {
	const response: ISaveDefaultDashboardResponse = await api.post<ISaveDefaultDashboardResponse>(
		"/api/Dashboard/SetAsDefault",
		{ Descriptor: dashboardDescriptor },
	);
	return response;
}

/**
 * Get nominated apps for given customer,
 *  including info about apps that are nominated but marked as not implementing now
 * @param orgId customer organization identifier
 * @returns
 */
export async function getNominatedApps(orgId: number): Promise<INominatedAppsForOrgResponseModel> {
	const response: INominatedAppsForOrgResponseModel = await api.get<INominatedAppsForOrgResponseModel>(
		`/api/NominatedApps/LoadAllForOrg?orgId=${orgId}`,
	);
	return response;
}

/**
 * Marks an app as not implementing now for a given org, or updates the not implementing now details.
 * @param appId client app ID
 * @param _orgID customer org ID, not used in API call directly, but passed here so it can be available to the success handler
 * @param privateReason reason only visible to AOPPs
 * @param publicReason reason visible to all users from the customer org
 * @param notifyInterestedUsers whether to notify interested/nominating users of this decision
 * @returns
 */
export async function markAsNotImplementingNow(
	appId: number,
	_orgId: number,
	privateReason: string,
	publicReason: string,
	notifyInterestedUsers: boolean,
	deleted: boolean,
): Promise<INotImplementingNowDecision | null> {
	const response = await api.post<INotImplementingNowDecision | null>(
		"/api/NominatedApps/MarkAsNotImplementingNow",
		{
			AppId: appId,
			PrivateReason: privateReason,
			PublicReason: publicReason,
			NotifyInterestedUsers: notifyInterestedUsers,
			Deleted: deleted,
		},
	);
	return response;
}

/**
 * Get in progress apps for given customer
 * @param orgId customer organization identifier
 * @returns
 */
export async function getInProgressApps(orgId: number): Promise<IInProgressAppsForOrgResponseModel> {
	const response: IInProgressAppsForOrgResponseModel = await api.get<IInProgressAppsForOrgResponseModel>(
		`/api/InProgressApps/LoadAllForOrg?orgId=${orgId}`,
	);
	return response;
}

/**
 * Get live apps for given customer
 * @param orgId customer organization identifier
 * @returns
 */
export async function getLiveApps(orgId: number): Promise<ILiveAppsForOrgResponseModel> {
	const response: ILiveAppsForOrgResponseModel = await api.get<ILiveAppsForOrgResponseModel>(
		`/api/LiveApps/LoadAllForOrg?orgId=${orgId}`,
	);
	return response;
}

/**
 * Get homegrown (customer created) apps for the customer
 * @param orgId customer organization identifier
 * @returns
 */
export async function getHomegrownApps(orgId: number): Promise<IHomegrownAppsForOrgResponseModel> {
	const response: IHomegrownAppsForOrgResponseModel = await api.get<IHomegrownAppsForOrgResponseModel>(
		`/api/HomegrownApps/LoadAllForOrg?orgId=${orgId}`,
	);
	return response;
}

/**
 * Create license-level info for a customer app, or update an existing license
 * @param data data to create or update
 * @returns created/updated license on success, throws error on failure
 */
export async function updateCustomerAppLicenseState(
	data: IUpdateCustomerAppLicenseRequest,
): Promise<IHomegrownAppsForOrgResponseModel> {
	const result: IHomegrownAppsForOrgResponseModel = await api.post<IHomegrownAppsForOrgResponseModel>(
		"/api/HomegrownApps/UpdateCustomerAppLicenseState",
		data,
	);

	return result;
}

/**
 * Get attestation data for the app
 * @param appId client application identifier
 * @returns
 */
export async function getAttestation(appId: number): Promise<IGetAttestationResponseModel> {
	const response: IGetAttestationResponseModel = await api.get<IGetAttestationResponseModel>(
		`/api/HomegrownApps/LoadAttestation?appId=${appId}`,
	);
	return response;
}

/**
 * Get all widgets known on the client as a map from the descriptor to the widget component.
 * When new widgets are created they should be added here and to the data.test.ts unit test file.
 */
export function getDefinedWidgets(): Map<string, IWidget> {
	const definedWidgets: Map<string, IWidget> = new Map([
		// [descriptor, component]
		[NominatedAppsWidget.descriptor, NominatedAppsWidget],
		[InProgressAppsWidget.descriptor, InProgressAppsWidget],
		[LiveAppsWidget.descriptor, LiveAppsWidget],
		[HomegrownAppsWidget.descriptor, HomegrownAppsWidget],
	]);
	return definedWidgets;
}
